import { useEffect } from "react";
import { CONCEPT_ORDER } from "@/concepts";
import { useConcept } from "@/features/concept-provider";
import { openQuickAdd, openTask, ui } from "@/features/ui-state";
import { taskRegistry } from "@/features/list/registry";
import { useTaskActions } from "@/data/actions";
import { isTypingTarget } from "@/lib/utils";
import { getLocation, navigate, type Route } from "@/router";
import { getStored, setStored } from "@/lib/storage";
import { FOCUS_MODE_KEY } from "@/features/today/focus-mode";
import { clearSelection, extendSelection, selectAllVisible, selectedTasks, visibleRowIds } from "@/features/selection/selection";
import { bulkActions } from "@/features/selection/BulkBar";
import { duplicateAndOpen } from "@/features/task/duplicate";

const rowIds = visibleRowIds;

function reveal(id: string) {
  const el = document.querySelector<HTMLElement>(`[data-task-id="${CSS.escape(id)}"]`);
  el?.scrollIntoView({ block: "nearest" });
}

const G_ROUTES: Record<string, Route> = {
  t: { name: "today" },
  i: { name: "inbox" },
  u: { name: "upcoming" },
  r: { name: "review" },
  z: { name: "snoozed" },
  a: { name: "all" },
  s: { name: "settings" },
};

/**
 * App-wide keyboard map (shared by every concept). The reference users see is `SHORTCUTS` in
 * ./shortcuts.ts; update it with any binding here.
 * ⌘N / q / c new task · ⌘K or / search · ⌘/ shortcuts · ⌘⇧1-3 concept · ⌘, settings · g+t/i/u/r/z/a/s navigation
 * j/k or ↓/↑ move · Enter open · Esc close · x or Space complete · ⌘⌫ delete
 * s status · p priority · h snooze · m move · t Today · ⇧F Focus mode (on Today)
 * ⌘/Ctrl-click or ⇧-click, ⇧↓/↑, ⌘A select several; x/Space, t, ⌘⌫ then act on all of them · ⌘D duplicate
 */
export function useGlobalShortcuts() {
  const { setConcept } = useConcept();
  const actions = useTaskActions();
  useEffect(() => {
    let gPending = 0;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      // Keyboard drags own the arrows, Space, Enter, and Escape until they drop or cancel.
      if (ui.get().dragging) return;
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();
      const state = ui.get();
      const anyModalOpen = !!document.querySelector('[role="dialog"][data-state="open"], [role="menu"][data-state="open"]');

      if (mod && !e.shiftKey && key === "n") {
        e.preventDefault();
        openQuickAdd();
        return;
      }
      if (mod && key === "k") {
        e.preventDefault();
        ui.set({ paletteOpen: !state.paletteOpen });
        return;
      }
      if (mod && e.shiftKey && ["1", "2", "3", "!", "@", "#"].includes(e.key)) {
        const idx = { "1": 0, "2": 1, "3": 2, "!": 0, "@": 1, "#": 2 }[e.key]!;
        e.preventDefault();
        setConcept(CONCEPT_ORDER[idx]!);
        return;
      }
      if (e.code && mod && e.shiftKey && /^Digit[123]$/.test(e.code)) {
        e.preventDefault();
        setConcept(CONCEPT_ORDER[Number(e.code.slice(5)) - 1]!);
        return;
      }
      if (mod && (key === "/" || e.code === "Slash")) {
        e.preventDefault();
        ui.set({ shortcutsOpen: !state.shortcutsOpen, paletteOpen: false });
        return;
      }
      if (mod && key === ",") {
        e.preventDefault();
        navigate({ name: "settings" });
        return;
      }
      if (isTypingTarget(e.target) || anyModalOpen) return;

      const sel = state.selectedTaskId;
      const task = sel ? taskRegistry.get(sel) : undefined;
      // With several rows selected, x/Space, t, and ⌘⌫ act on all of them.
      const multi = state.selectedIds.length > 1 ? selectedTasks() : null;
      const bulk = bulkActions(actions);

      if (mod && (e.key === "Backspace" || e.key === "Delete")) {
        if (multi) {
          e.preventDefault();
          void bulk.remove(multi);
        } else if (task) {
          e.preventDefault();
          void actions.remove(task);
        }
        return;
      }
      if (mod && !e.shiftKey && key === "d") {
        if (task && !multi) {
          e.preventDefault();
          void duplicateAndOpen(actions, task);
        }
        return;
      }
      if (mod && !e.shiftKey && key === "a") {
        e.preventDefault();
        selectAllVisible();
        return;
      }
      if (mod || e.altKey) return;

      if (gPending && Date.now() - gPending < 1200 && G_ROUTES[key]) {
        e.preventDefault();
        gPending = 0;
        navigate(G_ROUTES[key]!);
        return;
      }
      gPending = 0;

      if (e.shiftKey && ["J", "K", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        const id = extendSelection(e.key === "J" || e.key === "ArrowDown");
        if (id) reveal(id);
        return;
      }

      switch (e.key) {
        case "j":
        case "ArrowDown":
        case "k":
        case "ArrowUp": {
          const ids = rowIds();
          if (!ids.length) return;
          e.preventDefault();
          const down = e.key === "j" || e.key === "ArrowDown";
          const cur = sel ? ids.indexOf(sel) : -1;
          const next = cur === -1 ? (down ? 0 : ids.length - 1) : Math.max(0, Math.min(ids.length - 1, cur + (down ? 1 : -1)));
          ui.set({ selectedTaskId: ids[next]!, selectedIds: [], selectionAnchor: ids[next]! });
          reveal(ids[next]!);
          return;
        }
        case "Enter":
          if (sel) {
            e.preventDefault();
            openTask(state.openTaskId === sel ? null : sel);
          }
          return;
        case "Escape":
          if (state.openTaskId) openTask(null);
          else if (state.selectedIds.length) clearSelection();
          else ui.set({ selectedTaskId: null });
          return;
        case "x":
        case " ":
          if (multi) {
            e.preventDefault();
            void bulk.complete(multi);
          } else if (task) {
            e.preventDefault();
            void actions.toggleComplete(task);
          }
          return;
        case "q":
        case "c":
          e.preventDefault();
          openQuickAdd();
          return;
        case "/":
          e.preventDefault();
          ui.set({ paletteOpen: true });
          return;
        case "g":
          gPending = Date.now();
          return;
        case "s":
        case "p":
        case "h":
        case "m":
          if (task) {
            e.preventDefault();
            ui.set({ quickMenu: { kind: e.key === "s" ? "status" : e.key === "p" ? "priority" : e.key === "h" ? "snooze" : "move", task } });
          }
          return;
        case "F":
          // ⇧F: Focus mode on Today.
          if (e.shiftKey && getLocation().route.name === "today") {
            e.preventDefault();
            setStored(FOCUS_MODE_KEY, !getStored(FOCUS_MODE_KEY, false));
          }
          return;
        case "t":
          if (multi) {
            e.preventDefault();
            void bulk.today(multi);
          } else if (task && task.status !== "done" && task.status !== "cancelled") {
            e.preventDefault();
            void actions.toggleToday(task);
          }
          return;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [setConcept, actions]);

  // Electron: global shortcut → quick add, Help → Keyboard Shortcuts, notification clicks → route.
  useEffect(() => {
    const bridge = window.deskBridge;
    const offs = [
      bridge?.onQuickAdd?.(() => openQuickAdd()),
      bridge?.onShowShortcuts?.(() => ui.set({ shortcutsOpen: true, paletteOpen: false })),
      bridge?.onNavigate?.((route) => {
        if (/^#\/[\w/-]+$/.test(route)) window.location.hash = route;
      }),
    ];
    return () => offs.forEach((off) => off?.());
  }, []);
}
